// Keys a phone's on-screen keyboard lacks (Esc, Tab, a sticky Ctrl, the arrows, and / and ! for
// commands and bash), in a bar under the terminal on touch screens. Each sends the bytes xterm
// sends for the real key. The terminal and the bar also stay above the on-screen keyboard.
import type { Terminal } from "@xterm/xterm";

export type TouchKey = "escape" | "tab" | "up" | "down" | "left" | "right" | "slash" | "bang";
const ARROWS: Partial<Record<TouchKey, string>> = { up: "A", down: "B", right: "C", left: "D" };
const TEXT: Partial<Record<TouchKey, string>> = { escape: "\x1b", tab: "\t", slash: "/", bang: "!" };

/** xterm's bytes for a key: arrows follow application cursor mode, and Ctrl with an arrow moves by word. */
export function keySequence(key: TouchKey, { ctrl = false, applicationCursor = false } = {}): string {
	const arrow = ARROWS[key];
	if (!arrow) return TEXT[key]!;
	if (ctrl) return `\x1b[1;5${arrow}`;
	return `${applicationCursor ? "\x1bO" : "\x1b["}${arrow}`;
}

/** Input typed after the sticky Ctrl: a letter, @ [ \ ] ^ _ or space becomes its control character. */
export function withControl(data: string): string {
	const first = data[0];
	if (first === undefined) return data;
	const code = first === " " ? 0x40 : first.toUpperCase().charCodeAt(0);
	if (first === "?") return `\x7f${data.slice(1)}`;
	return code >= 0x40 && code <= 0x5f ? String.fromCharCode(code & 0x1f) + data.slice(1) : data;
}

/** How much of the window's bottom the on-screen keyboard covers, from the visual viewport. */
export function keyboardInset(innerHeight: number, viewport: Pick<VisualViewport, "height" | "offsetTop" | "scale"> | null | undefined): number {
	// Pinch zoom shrinks the visual viewport too, without any keyboard.
	if (!viewport || Math.abs(viewport.scale - 1) > 0.01) return 0;
	return Math.max(0, Math.round(innerHeight - viewport.height - viewport.offsetTop));
}

/** Keep `--pi-keyboard-inset` (CSS lifts the terminal, the key bar and phone dialogs by it) up to date. */
export function trackKeyboard(): void {
	const viewport = window.visualViewport;
	const update = () => document.documentElement.style.setProperty("--pi-keyboard-inset", `${keyboardInset(window.innerHeight, viewport)}px`);
	viewport?.addEventListener("resize", update);
	viewport?.addEventListener("scroll", update);
	window.addEventListener("resize", update);
	update();
}

// Symbols get a spoken name; a word is its own name, so voice control can say what is on the key.
const KEYS: Array<{ key: TouchKey | "ctrl"; label: string; name?: string }> = [
	{ key: "escape", label: "Esc" },
	{ key: "tab", label: "Tab" },
	{ key: "ctrl", label: "Ctrl" },
	{ key: "up", label: "↑", name: "Up arrow" },
	{ key: "down", label: "↓", name: "Down arrow" },
	{ key: "left", label: "←", name: "Left arrow" },
	{ key: "right", label: "→", name: "Right arrow" },
	{ key: "slash", label: "/", name: "Slash" },
	{ key: "bang", label: "!", name: "Exclamation mark" },
];

export interface TouchKeysOptions {
	term: Terminal;
	input(data: string): void;
	/** Route typed text through `filter` before pi reads it; the sticky Ctrl uses it. */
	filterInput(filter: (data: string) => string): void;
}

/** The bar under the terminal while the main pointer is coarse (a finger), kept in sync if that changes. */
export function installTouchKeys({ term, input, filterInput }: TouchKeysOptions): HTMLElement {
	const bar = document.createElement("div");
	bar.id = "pi-touch-keys"; bar.setAttribute("role", "group"); bar.setAttribute("aria-label", "Terminal keys");
	let ctrl = false;
	const ctrlButton = document.createElement("button");
	const setCtrl = (on: boolean) => { ctrl = on; ctrlButton.setAttribute("aria-pressed", String(on)); };
	for (const { key, label, name } of KEYS) {
		const button = key === "ctrl" ? ctrlButton : document.createElement("button");
		button.type = "button"; button.className = "pi-btn"; button.textContent = label; button.dataset.key = key;
		if (name) button.setAttribute("aria-label", name);
		bar.append(button);
	}
	setCtrl(false);
	const press = (button: HTMLButtonElement) => {
		const key = button.dataset.key as TouchKey | "ctrl";
		if (key === "ctrl") setCtrl(!ctrl);
		else { input(keySequence(key, { ctrl, applicationCursor: term.modes.applicationCursorKeysMode })); setCtrl(false); }
	};
	const buttonOf = (event: Event) => (event.target as Element).closest<HTMLButtonElement>("#pi-touch-keys button");
	// A finger on a key must not move focus out of the terminal: that would close the keyboard. Safari
	// then sends no click at all, so a pointer press acts on pointerup; click is left to the keyboard
	// and to screen readers.
	let pressed: { button: HTMLButtonElement; pointer: number } | undefined, clickHandled = false;
	bar.addEventListener("pointerdown", (event) => {
		const button = buttonOf(event);
		if (!button || event.button !== 0) return;
		event.preventDefault();
		pressed = { button, pointer: event.pointerId };
	});
	bar.addEventListener("pointerup", (event) => {
		const button = buttonOf(event), was = pressed; pressed = undefined;
		if (!button || button !== was?.button || event.pointerId !== was.pointer) return;
		press(button);
		// Chrome and Firefox still send the click; it must not press the key twice.
		clickHandled = true; setTimeout(() => { clickHandled = false; }, 600);
	});
	bar.addEventListener("pointercancel", () => { pressed = undefined; });
	bar.addEventListener("mousedown", (event) => event.preventDefault());
	bar.addEventListener("click", (event) => {
		const button = buttonOf(event);
		if (!button) return;
		if (clickHandled) { clickHandled = false; return; }
		press(button);
	});
	filterInput((data) => {
		if (!ctrl) return data;
		setCtrl(false);
		return withControl(data);
	});
	const coarse = window.matchMedia("(pointer: coarse)");
	const show = () => {
		bar.hidden = !coarse.matches;
		document.documentElement.classList.toggle("pi-touch-keys", coarse.matches);
		if (!coarse.matches) setCtrl(false);
	};
	coarse.addEventListener("change", show);
	show();
	document.body.append(bar);
	return bar;
}
